// ---------------------------------------------------------------------------
// Prezio Onboard Widget — Constants
// ---------------------------------------------------------------------------

import type { StepId, SessionStatus } from '@/api/types';
import type { ColorScheme, Locale, ThemeConfig } from '@/types/config';

// ---------------------------------------------------------------------------
// Step ordering
// ---------------------------------------------------------------------------

export const STEP_ORDER: readonly StepId[] = [
  'country',
  'address',
  'address_validation',
  'local_consumer_type',
  'retailer_org',
  'retailer_main_tariff',
  'retailer_signup_date',
  'retailer_tariff',
  'retailer_properties',
  'retailer_custom_type',
  'retailer_custom_properties',
  'retailer_custom_prices',
  'dso_org',
  'dso_main_tariff',
  'dso_tariff',
  'dso_properties',
  'authorities_conditions',
  'authorities',
  'summary',
] as const;

// ---------------------------------------------------------------------------
// Terminal statuses
// ---------------------------------------------------------------------------

/** Statuses that mean the session cannot accept more input. */
export const TERMINAL_STATUSES: readonly SessionStatus[] = [
  'completed',
  'cancelled',
  'expired',
  'failed',
] as const;

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

export const DEFAULT_API_BASE_URL = 'https://api.prezio.eu';

export const DEFAULT_LOCALE = 'en' satisfies Locale;

export const DEFAULT_COLOR_SCHEME: ColorScheme = 'light';

// ---------------------------------------------------------------------------
// Currency display
// ---------------------------------------------------------------------------

/** Map ISO 4217 codes to their common symbols. Falls back to raw code. */
export const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€', GBP: '£', USD: '$', PLN: 'zł', CZK: 'Kč', HUF: 'Ft',
  SEK: 'kr', DKK: 'kr', NOK: 'kr', CHF: 'CHF', BGN: 'лв', RON: 'lei',
};

export const DEFAULT_THEME: ThemeConfig = {
  colorScheme: DEFAULT_COLOR_SCHEME,
} as const;

// ---------------------------------------------------------------------------
// Predefined-step helpers
// ---------------------------------------------------------------------------

/**
 * Return the subset of STEP_ORDER that excludes predefined (hidden) steps.
 * Accepts the raw array from `session.predefined_steps` — gracefully handles
 * `undefined` / `null` for backward-compat with older backend responses.
 */
export function getVisibleSteps(predefinedSteps: StepId[] | undefined | null): readonly StepId[] {
  if (!predefinedSteps || predefinedSteps.length === 0) return STEP_ORDER;
  const hidden = new Set<StepId>(predefinedSteps);
  return STEP_ORDER.filter((s) => !hidden.has(s));
}

/**
 * Build a `Set<StepId>` from the session's `predefined_steps` for O(1) lookups.
 */
export function getPredefinedSet(predefinedSteps: StepId[] | undefined | null): Set<StepId> {
  return new Set<StepId>(predefinedSteps ?? []);
}
